import type { UploadFile } from "antd/es/upload/interface"

import { validateFile } from "../../../utils/file-validation.ts"
import { type LeadFormData } from "../LeadAddPage/components/lead-form-data.ts"
import { type LeadAttachment } from "../types/lead.types.ts"

const MAX_SIZE_MB = 50
const ALLOWED_FILE_EXTENSIONS = [
  "png",
  "jpeg",
  "jpg",
  "pdf",
  "mp4",
  "mov",
  "webm",
  "doc",
  "docx"
]

const LEAD_UPLOAD_ACCEPT = ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(
  ","
)

const validateLeadUpload = (file: File) =>
  validateFile(file, {
    maxSize: MAX_SIZE_MB,
    allowedExtensions: ALLOWED_FILE_EXTENSIONS
  })

const LEAD_ATTACHMENT_FILE_TYPE = 5

/**
 * The lead route reads uploads from `file[N].file` / `file[N].file_type`, and
 * `createFormData` only emits those keys for an entry that carries a
 * `file_type` — a bare upload list serialises to a `file` key nothing on the
 * server reads, which is a 200 that saves nothing. antd fires `onChange` once
 * per selected file with the cumulative list, so only the changed entry
 * travels; resending the list would upload what already saved a second time.
 */
const leadUploadPatch = (
  changed: UploadFile,
  fileList: UploadFile[]
): Pick<LeadFormData, "file_list"> | null => {
  const entry = fileList.find(
    (file) => file?.uid === changed?.uid && !!file?.originFileObj
  )

  return entry
    ? { file_list: [{ file: entry, file_type: LEAD_ATTACHMENT_FILE_TYPE }] }
    : null
}

const firstFilled = (...values: Array<unknown>): string | null => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value
  }
  return null
}

const visibleLeadAttachments = (
  attachments?: LeadAttachment[] | null
): LeadAttachment[] => (attachments ?? []).filter((file) => !file?.is_archived)

const attachmentName = (attachment?: LeadAttachment | null): string =>
  firstFilled(attachment?.original_filename, attachment?.file_name) ??
  "document"

const attachmentExtension = (
  attachment?: LeadAttachment | null
): string | null => {
  const name = attachmentName(attachment)
  const extension = name.includes(".") ? name.split(".").pop() : null
  return extension ? extension.toUpperCase() : null
}

/** A raw MIME string is never shown — only ever a short, human label. */
const MIME_TYPE_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "XLSX",
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "video/mp4": "MP4",
  "video/quicktime": "MOV",
  "video/webm": "WEBM"
}

/** The filename's own extension is the cleanest source (it is always short
 * and already what the user typed); `file_type_name` is a real backend
 * label when present; a MIME lookup only stands in for a file with neither.
 * `mime_type` itself, and the numeric `file_type` code, are never shown raw. */
const attachmentType = (attachment?: LeadAttachment | null): string | null => {
  const extension = attachmentExtension(attachment)
  if (extension) return extension === "JPEG" ? "JPG" : extension

  const label = firstFilled(attachment?.file_type_name)
  if (label) return label

  const mime = attachment?.mime_type
  return (mime && MIME_TYPE_LABELS[mime]) || null
}

const IMAGE_EXTENSIONS = new Set(["PNG", "JPEG", "JPG"])
const DOCUMENT_EXTENSIONS = new Set(["PDF", "DOC", "DOCX"])

type AttachmentCategory = "images" | "documents" | "other"

/** The Files tab's Type filter — three buckets, derived from the same
 * extension/mime data the Type column already reads, nothing new. */
const attachmentCategory = (
  attachment?: LeadAttachment | null
): AttachmentCategory => {
  const mime = attachment?.mime_type ?? ""
  const extension = attachmentExtension(attachment) ?? ""

  if (mime.startsWith("image/") || IMAGE_EXTENSIONS.has(extension))
    return "images"
  if (mime === "application/pdf" || DOCUMENT_EXTENSIONS.has(extension))
    return "documents"
  return "other"
}

const isImageAttachment = (attachment?: LeadAttachment | null): boolean =>
  attachmentCategory(attachment) === "images"

const FILE_SIZE_UNITS = ["B", "KB", "MB", "GB", "TB"]

/** Mirrors the API's own `formatted_file_size`, for payloads that omit it. */
const formatFileSize = (bytes: number): string => {
  let size = bytes
  let unit = 0

  while (size >= 1024 && unit < FILE_SIZE_UNITS.length - 1) {
    size /= 1024
    unit += 1
  }

  return `${size.toFixed(1)} ${FILE_SIZE_UNITS[unit]}`
}

const attachmentSize = (attachment?: LeadAttachment | null): string | null => {
  const formatted = firstFilled(attachment?.formatted_file_size)
  if (formatted) return formatted

  const bytes = attachment?.file_size
  return typeof bytes === "number" && bytes >= 0 ? formatFileSize(bytes) : null
}

const attachmentUploadedBy = (
  attachment?: LeadAttachment | null
): string | null =>
  firstFilled(attachment?.created_by_name, attachment?.uploaded_by)

const attachmentDate = (attachment?: LeadAttachment | null): string | null =>
  firstFilled(attachment?.created)

export {
  type AttachmentCategory,
  ALLOWED_FILE_EXTENSIONS,
  LEAD_ATTACHMENT_FILE_TYPE,
  LEAD_UPLOAD_ACCEPT,
  MAX_SIZE_MB,
  attachmentCategory,
  attachmentDate,
  attachmentExtension,
  attachmentName,
  attachmentSize,
  attachmentType,
  attachmentUploadedBy,
  formatFileSize,
  isImageAttachment,
  leadUploadPatch,
  validateLeadUpload,
  visibleLeadAttachments
}
